import { useEffect, useState } from 'react';
import type { Session } from '../App';
import { Container, PageHeader } from '../App';
import {
  api,
  PlayerCharacter,
  PlayerHero,
  PlayerProfile,
  PlayerStatus,
} from '../api';

interface Props { session: Session; }

export default function Players({ session }: Props) {
  const [players, setPlayers] = useState<PlayerProfile[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [characters, setCharacters] = useState<PlayerCharacter[]>([]);
  const [heroes, setHeroes] = useState<PlayerHero[]>([]);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    setError(null);
    try {
      const list = (await api.listPlayers(session.gameId)).players;
      setPlayers(list);
      if (selectedId && !list.some(p => p.player_id === selectedId)) {
        setSelectedId(list[0]?.player_id ?? null);
      } else if (!selectedId && list.length > 0) {
        setSelectedId(list[0].player_id);
      }
    } catch (e) {
      setError((e as Error).message);
    }
  };

  useEffect(() => {
    reload();
  }, [session.gameId]);

  useEffect(() => {
    if (!selectedId) {
      setCharacters([]);
      setHeroes([]);
      return;
    }
    Promise.all([
      api.listCharacters(session.gameId, selectedId),
      api.listHeroes(session.gameId, selectedId),
    ])
      .then(([c, h]) => {
        setCharacters(c.characters);
        setHeroes(h.heroes);
      })
      .catch(e => setError((e as Error).message));
  }, [session.gameId, selectedId]);

  const setStatus = async (playerId: string, status: PlayerStatus) => {
    try {
      await api.setPlayerStatus(session.gameId, playerId, status);
      await reload();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const selected = players.find(p => p.player_id === selectedId) ?? null;

  return (
    <Container>
      <PageHeader
        title="Players"
        subtitle="Runtime players, characters, and hero rosters for this game. Read-only — players, characters, and heroes are created at runtime by your game server via the REST API."
      />

      {error && <div className="card border-red-500/40 mb-4 text-red-500">{error}</div>}

      <div className="card mb-4">
        <div className="font-medium mb-3">All players ({players.length})</div>
        {players.length === 0 ? (
          <div className="empty text-sm">No players yet.</div>
        ) : (
          <div className="table-wrap">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-fg-muted text-xs uppercase">
                  <th className="py-1.5">Name</th>
                  <th className="py-1.5">Player ID</th>
                  <th className="py-1.5">Realm</th>
                  <th className="py-1.5">External ID</th>
                  <th className="py-1.5">Status</th>
                  <th className="py-1.5">Created</th>
                  <th className="py-1.5"></th>
                </tr>
              </thead>
              <tbody>
                {players.map(p => (
                  <tr
                    key={p.player_id}
                    className={[
                      'border-t border-line cursor-pointer hover:bg-bg-2',
                      p.player_id === selectedId ? 'bg-bg-2' : '',
                    ].join(' ')}
                    onClick={() => setSelectedId(p.player_id)}
                  >
                    <td className="py-2">{p.display_name}</td>
                    <td className="py-2 text-xs"><code>{p.player_id}</code></td>
                    <td className="py-2 text-xs">{p.realm_id}</td>
                    <td className="py-2 text-xs">{p.external_user_id ?? '—'}</td>
                    <td className="py-2">
                      <span className={`badge ${
                        p.status === 'active' ? 'badge-ok'
                        : p.status === 'disabled' ? 'badge-warn'
                        : 'badge-muted'
                      }`}>
                        {p.status}
                      </span>
                    </td>
                    <td className="py-2 text-xs">{new Date(p.created_at).toLocaleString()}</td>
                    <td className="py-2 text-right" onClick={e => e.stopPropagation()}>
                      {p.status === 'active' ? (
                        <button
                          className="btn btn-ghost text-xs"
                          onClick={() => setStatus(p.player_id, 'disabled')}
                        >
                          Disable
                        </button>
                      ) : (
                        <button
                          className="btn btn-ghost text-xs"
                          onClick={() => setStatus(p.player_id, 'active')}
                        >
                          Enable
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selected && (
        <>
          <h2 className="text-base font-semibold mb-2 mt-2">
            Characters for <span className="text-fg-dim">{selected.display_name}</span>
          </h2>
          <div className="card mb-4">
            {characters.length === 0 ? (
              <div className="empty text-sm">No characters yet.</div>
            ) : (
              <div className="table-wrap">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-fg-muted text-xs uppercase">
                      <th className="py-1.5">Name</th>
                      <th className="py-1.5">Character ID</th>
                      <th className="py-1.5">Archetype</th>
                      <th className="py-1.5">Primary</th>
                      <th className="py-1.5">Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {characters.map(c => (
                      <tr key={c.character_id} className="border-t border-line">
                        <td className="py-2">{c.display_name}</td>
                        <td className="py-2 text-xs"><code>{c.character_id}</code></td>
                        <td className="py-2 text-xs">{c.archetype_ref ?? '—'}</td>
                        <td className="py-2 text-xs">{c.is_primary ? 'yes' : ''}</td>
                        <td className="py-2 text-xs">{new Date(c.created_at).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <h2 className="text-base font-semibold mb-2 mt-2">
            Heroes for <span className="text-fg-dim">{selected.display_name}</span>
          </h2>
          <div className="card">
            {heroes.length === 0 ? (
              <div className="empty text-sm">No heroes unlocked.</div>
            ) : (
              <div className="table-wrap">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-fg-muted text-xs uppercase">
                      <th className="py-1.5">Hero</th>
                      <th className="py-1.5">Instance ID</th>
                      <th className="py-1.5">Level</th>
                      <th className="py-1.5">Rank</th>
                      <th className="py-1.5">Active</th>
                      <th className="py-1.5">Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {heroes.map(h => (
                      <tr key={h.hero_instance_id} className="border-t border-line">
                        <td className="py-2">{h.hero_ref}</td>
                        <td className="py-2 text-xs"><code>{h.hero_instance_id}</code></td>
                        <td className="py-2 text-xs">{h.level}</td>
                        <td className="py-2 text-xs">{h.rank}</td>
                        <td className="py-2 text-xs">{h.active ? 'yes' : ''}</td>
                        <td className="py-2 text-xs">{new Date(h.created_at).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </Container>
  );
}
